import http from './http';

export interface JobSite {
  id: string;
  // Job is user-scoped (owner directive 2026-09-23); workspace_id is legacy/optional.
  workspace_id?: string | null;
  user_id?: string | null;
  name: string;
  url?: string;
  search_query?: string;
  is_active: string;
  // Sector label derived from the company name (see backend job_categories).
  category?: string | null;
  // Ownership + compatibility (owner directive 2026-09-23).
  scope?: 'system' | 'user';
  compatibility?: 'full_agent' | 'partial_plugin' | 'partial_manual' | 'incompatible' | null;
  approval_status?: 'local' | 'pending_review' | 'approved' | 'declined';
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_note?: string | null;
  submitted_for_review_at?: string | null;
  // Per-site "how to apply here" recipe (owner directive 2026-09-28): the steps,
  // caveats and workarounds the plug-in/agent follows at submit time.
  submission_instructions?: string | null;
  created_by: string;
  created_at: string;
}

/** The four-tier verdict for a job board URL. */
export interface SiteCompatibilityVerdict {
  url: string;
  tier: 'full_agent' | 'partial_plugin' | 'partial_manual' | 'incompatible';
  label: string;
  verified: boolean;
  reachable: boolean | null;
  can_auto_apply: boolean;
  detail: string;
  error?: string | null;
}

export interface JobSiteCategory {
  category: string;
  count: number;
  active?: number;
  site_ids: string[];
}

export interface LeadApplicationSummary {
  id: string;
  status: string;
  approval_status?: string | null;
  submission_channel?: string | null;
  submission_detail?: string | null;
  prepared_at?: string | null;
  submitted_at?: string | null;
  applied_at?: string | null;
  resume_variant_id?: string | null;
}

/** One human word for a lead's apply state, from its application row. */
export type LeadApplyState =
  | 'submitted'
  | 'awaiting_approval'
  | 'prepared'
  | 'rejected'
  | 'recorded'
  | null;

export interface JobLead {
  id: string;
  workspace_id: string;
  site_id?: string;
  title: string;
  company?: string;
  location?: string;
  salary_range?: string;
  description?: string;
  url?: string;
  status: string;
  created_at: string;
  // Lead lifecycle (cleanup pass, owner directive 2026-09-23).
  status_reason?: string | null;
  last_checked_at?: string | null;
  check_error?: string | null;
  // Every lead belongs to a job hunt (owner directive 2026-09-22).
  search_id?: string | null;
  search_name?: string | null;
  match_score?: number | null;
  // Apply state, derived from this lead's application rows (never guessed).
  applications?: LeadApplicationSummary[];
  application_status?: LeadApplyState;
}

export interface LeadCleanupAction {
  lead_id: string;
  title: string;
  company?: string | null;
  bucket: 'declined' | 'closed' | 'stale';
  from_status: string;
  to_status: string;
  reason: string;
  detail: string;
}

export interface LeadCleanupResult {
  workspace_id: string;
  dry_run: boolean;
  checked: number;
  unverifiable: number;
  check_errors: number;
  healthy: number;
  stale_days: number;
  counts: { declined: number; closed: number; stale: number; total: number };
  actions: LeadCleanupAction[];
  summary: string;
}

export interface PreparedApplication {
  ok: boolean;
  already_prepared: boolean;
  application_id: string;
  resume_variant_id?: string | null;
  approval_status: string;
  submission_channel?: string;
  detail: string;
}

export interface JobApplication {
  id: string;
  workspace_id: string;
  lead_id?: string;
  resume_variant_id?: string;
  cover_letter?: string;
  status: string;
  approval_status?: string;
  submission_channel?: string;
  submission_detail?: string;
  // Honest post-submit state (owner 2026-10-01): 'verified' only with real
  // evidence, otherwise 'submitted_unverified'. Absent = not submitted yet.
  verification_status?: string | null;
  // Which connected mailbox sends an email application (owner directive 2026-09-27).
  apply_email_account_id?: string | null;
  prepared_at?: string;
  submitted_at?: string;
  applied_at: string | null;
}

// Proof of a real submission (owner 2026-10-01) — what/when/where, so the UI can
// answer "how do we know it really applied?".
export interface ApplicationProof {
  ok?: boolean;
  application_id: string;
  lead_id?: string;
  status?: string;
  approval_status?: string;
  channel?: string | null;
  submitted_at?: string | null;
  verification_status?: string | null;
  verified_at?: string | null;
  evidence?: Record<string, unknown>;
  submission_detail?: string | null;
  journal?: Array<{ at?: string; message?: string; step?: string }>;
  why_no_confirmation?: string | null;
}

export interface PendingApplication {
  id: string;
  lead_id?: string;
  lead_title?: string;
  company?: string;
  resume_variant_id?: string;
  status: string;
  approval_status: string;
  submission_channel?: string;
  submission_detail?: string;
  prepared_at?: string;
}

/**
 * One row of the guided job-site connection registry (owner directive 2026-09-28;
 * reworked the same day for **local-only credentials**).  Metadata only — the
 * plug-in keeps any secret in `chrome.storage.local`; nothing sensitive is here.
 */
export interface JobSiteConnection {
  site?: string | null;
  label: string;
  domain: string;
  site_url?: string | null;
  login_url?: string | null;
  status: 'connected' | 'needs_login' | 'expired' | 'failed' | 'revoked';
  connected: boolean;
  connection_id?: string | null;
  account_label?: string | null;
  account_hint?: string | null;
  /** always 'local' — the secret is held by the plug-in on the device. */
  auth_origin?: string | null;
  last_verified_at?: string | null;
  last_seen_at?: string | null;
  expires_at?: string | null;
  expires_soon?: boolean;
  expired?: boolean;
  renew_hint?: string | null;
  notes?: string | null;
}

export interface JobSiteConnectionOverview {
  sites: JobSiteConnection[];
  connected_count: number;
  needs_login_count: number;
  expired_count?: number;
  needs_renewal_count?: number;
  total: number;
}

/** Read-only job-site auth-status board (flag-gated backend endpoint). */
export interface SiteStatusBoardCounts {
  utilizable: number;
  needs_verification: number;
  unavailable: number;
  connected: number;
  needs_action: number;
  total: number;
}

export interface SiteStatusBoardSite {
  site_id: string;
  domain?: string;
  name?: string;
  ability?: string;
  auth_status?: string;
  credential_type?: string;
  needs_action?: boolean;
  action_label?: string | null;
  position?: number;
}

export interface SiteStatusBoardResponse {
  ok: boolean;
  enabled: boolean;
  counts: SiteStatusBoardCounts;
  sites: SiteStatusBoardSite[];
}

/** Read-only in-app apply-readiness summary (flag-gated backend endpoint). */
export interface ApplyRunSummary {
  would_submit: number;
  blocked: number;
  skipped: number;
  total: number;
}

/** Response of GET /job-connections/apply-run-plan (read-only, flag-gated). */
export interface ApplyRunPlanResponse {
  ok: boolean;
  enabled: boolean;
  // Absent while the flag is OFF (the endpoint short-circuits before any work).
  items?: Array<Record<string, unknown>>;
  summary?: ApplyRunSummary;
}

/** Read-only guided sign-in verification queue (flag-gated backend endpoint). */
export interface VerificationQueueStep {
  index: number;
  site_id: string;
  domain?: string;
  step: string;
  action: string;
  credential_type_hint: 'login_password' | 'session_token' | 'undetermined';
  status: string;
}

export interface VerificationQueueCounts {
  total: number;
  remaining: number;
  completed_before_resume: number;
}

export interface VerificationQueueResponse {
  ok: boolean;
  enabled: boolean;
  steps: VerificationQueueStep[];
  counts: VerificationQueueCounts;
  resume_after_site_id?: string | null;
}

/** Read-only legacy plug-in queue-migration plan (flag-gated backend endpoint). */
export interface QueueMigrationSummary {
  requeue_in_app: number;
  leave_as_is: number;
  unchanged: number;
  blocked: number;
  total: number;
}

export interface QueueMigrationItem {
  application_id: string;
  from_channel: string;
  to_channel: string;
  action: string;
  blocked: boolean;
  reason?: string | null;
}

export interface QueueMigrationResponse {
  ok: boolean;
  enabled: boolean;
  // Absent while the flag is OFF (the endpoint short-circuits before any work).
  summary?: QueueMigrationSummary;
  items?: QueueMigrationItem[];
}

/** Read-only legacy plug-in token-retirement plan (flag-gated backend endpoint). */
export interface ExtensionRetirementCounts {
  revoke: number;
  leave: number;
  total: number;
}

export interface ExtensionRetirementItem {
  token_id: string;
  // Non-secret display prefix only; the token hash is never surfaced.
  token_prefix?: string;
  state: string;
  action: string;
  reason?: string | null;
}

export interface ExtensionRetirementResponse {
  ok: boolean;
  enabled: boolean;
  // Absent while the flag is OFF (the endpoint short-circuits before any work).
  counts?: ExtensionRetirementCounts;
  items?: ExtensionRetirementItem[];
}

/**
 * Read-only in-app GO-LIVE READINESS report (flag-gated backend endpoint).
 * Mirrors app/scripts/job_sites_auth_preflight.build_report exactly. Only NAMES,
 * booleans and counts are surfaced — never a secret/token/password value.
 */
export type GoLiveReadinessState = 'OK' | 'WARN' | 'FATAL';

export interface GoLiveAlembicReport {
  expected_head: string | null;
  repo_heads: string[] | null;
  heads_ok: boolean;
  expected_prod_current: string | null;
  prod_current: string | null;
  current_ok: boolean;
  fatal: string[];
  warn: string[];
  info: string[];
}

export interface GoLiveFlagRow {
  field: string;
  env: string;
  helper_module: string;
  helper: string;
  field_value: boolean | null;
  helper_value: boolean | null;
  live_effect: boolean;
  state: string;
  route: string;
}

export interface GoLiveFlagsReport {
  rows: GoLiveFlagRow[];
  // (field, helper_name, live_effect) tuples in the report; only the count is shown.
  armed: Array<[string, string, boolean]>;
  fatal: string[];
  warn: string[];
}

export interface GoLiveRoutesReport {
  app_ok: boolean;
  expected: string[];
  registered: string[];
  missing: string[];
  fatal: string[];
}

/**
 * Response of GET /job-connections/go-live-readiness (read-only, flag-gated).
 * While SAIK_GO_LIVE_READINESS_ENABLED is OFF the endpoint returns the inert
 * `{ ok: true, enabled: false }` (no report fields). When ON it returns the raw
 * report body (state/fatal/warn/info/alembic/flags/routes/live — no `enabled`).
 */
export interface GoLiveReadinessResponse {
  // Present only in the flag-OFF inert shape.
  ok?: boolean;
  enabled?: boolean;
  // Present only when the flag is ON (the report body).
  state?: GoLiveReadinessState;
  fatal?: string[];
  warn?: string[];
  info?: string[];
  alembic?: GoLiveAlembicReport;
  flags?: GoLiveFlagsReport;
  routes?: GoLiveRoutesReport;
  // Always null via this endpoint (build_report(include_live=False)).
  live?: unknown;
}

export interface ResumeDocument {
  id: string;
  workspace_id: string;
  title: string;
  content?: string;
  file_path?: string;
  created_by: string;
  created_at: string;
  parse_status?: string;
  is_primary?: boolean;
}

export interface ResumeBuildExperience {
  title?: string;
  company?: string;
  dates?: string;
  bullets?: string;
}

export interface ResumeBuildEducation {
  degree?: string;
  school?: string;
  dates?: string;
  detail?: string;
}

export interface ResumeBuildPayload {
  workspace_id?: string;
  title?: string;
  use_model?: boolean;
  full_name?: string;
  headline?: string;
  email?: string;
  phone?: string;
  location?: string;
  summary?: string;
  links?: string[];
  skills?: string[];
  experience?: ResumeBuildExperience[];
  education?: ResumeBuildEducation[];
}

export interface ResumeWriteResult {
  ok: boolean;
  document_id: string;
  source_document_id?: string;
  title: string;
  generation_method: 'llm' | 'heuristic';
  change_notes: string;
  content: string;
}

export interface ResumeSyncReport {
  ok: boolean;
  documents_considered: number;
  documents_updated: number;
  updated: Array<{ document_id: string; title: string; fields: string[] }>;
  skipped: Array<{ document_id: string; title: string; reason: string }>;
  fields_synced: string[];
  note?: string;
}

export interface ResumeVariant {
  id: string;
  workspace_id: string;
  document_id?: string;
  name: string;
  tailored_content?: string;
  created_at: string;
}

// Named resume profile — one per job type a user hunts (owner directive 2026-10-12).
export interface ResumeProfile {
  id: string;
  workspace_id?: string | null;
  user_id: string;
  name: string;
  job_types: string[];
  summary_hint?: string | null;
  base_document_id?: string | null;
  base_document_name?: string | null;
  hype_level: string;
  is_default: boolean;
  created_by?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DiscoverySiteResult {
  site_id: string;
  site_name: string;
  query: string;
  found: number;
  created: number;
  skipped_duplicates: number;
  skipped_invalid: number;
  skipped_location?: number;
  skipped_role?: number;
  error?: string | null;
}

export interface DiscoveryResult {
  workspace_id: string;
  sites_scanned: number;
  sites_skipped: number;
  found: number;
  created: number;
  skipped_duplicates: number;
  skipped_invalid: number;
  skipped_location?: number;
  skipped_role?: number;
  errors: string[];
  per_site: DiscoverySiteResult[];
  created_leads: Array<{ id: string; title: string; url: string }>;
  summary: string;
}

export interface DiscoverProgressSite {
  site_id?: string | null;
  site_name?: string | null;
  found: number;
  created: number;
  skipped_location?: number;
  skipped_role?: number;
  error?: string | null;
  source?: string | null;
}

/** Live status of an in-flight "Find new leads" scan (polled while the POST runs). */
export interface DiscoverProgress {
  state: 'running' | 'done' | 'error';
  started_at: number;
  updated_at: number;
  total_sites: number;
  sites_done: number;
  current_site?: string | null;
  current_index: number;
  found: number;
  created: number;
  skipped_duplicates: number;
  skipped_location?: number;
  skipped_role?: number;
  errors: number;
  per_site: DiscoverProgressSite[];
  summary: string;
}

export interface JobInterview {
  id: string;
  workspace_id: string;
  application_id?: string;
  lead_id?: string;
  correspondence_id?: string;
  title?: string;
  company?: string;
  scheduled_at?: string;
  ends_at?: string;
  location?: string;
  mode?: string;
  interviewer?: string;
  status: string;
  source: string;
  raw_when?: string;
  notes?: string;
  notified_at?: string;
  created_at: string;
}

export interface JobCorrespondence {
  id: string;
  application_id?: string;
  lead_id?: string;
  direction: string;
  category: string;
  from_addr?: string;
  subject?: string;
  body_excerpt?: string;
  draft_reply?: string;
  classification_method?: string;
  confidence?: number;
  status: string;
  send_detail?: string;
  created_at: string;
}

export interface ProfileExperience {
  title?: string;
  company?: string;
  dates?: string;
  bullets?: string;
}

export interface ApplicantProfile {
  id: string;
  workspace_id: string;
  user_id: string;
  full_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  headline?: string;
  talent_summary?: string;
  skills: string[];
  experience_summary?: string;
  experience?: ProfileExperience[];
  target_roles: string[];
  target_fields: string[];
  target_locations: string[];
  seniority?: string;
  years_experience?: number;
  links: string[];
  source_document_id?: string;
  extraction_method: string;
  extraction_notes?: string;
  updated_at?: string;
}

export interface HypeLevel {
  value: string;
  label: string;
}

export interface JobSearch {
  hype_level?: string;
  id: string;
  workspace_id: string;
  user_id: string;
  name: string;
  field?: string;
  keywords?: string;
  location?: string;
  sites: string[];
  resume_document_id?: string;
  resume_variant_id?: string;
  // Named resume profile for this hunt (owner directive 2026-10-12).
  resume_profile_id?: string | null;
  resume_strategy: 'tailor_per_job' | 'fixed_resume';
  is_active: boolean;
  autonomy_mode: string;
  daily_cap: number;
  // Structured search fields (owner directive 2026-09-29)
  seniority?: string | null;
  employment_type?: string | null;
  work_mode?: string | null;
  relocation?: boolean | null;
  radius_km?: number | null;
  date_posted?: string | null;
  exclude_keywords?: string | null;
  include_companies?: string | null;
  exclude_companies?: string | null;
  exclude_agencies?: boolean | null;
  salary_min_cents?: number | null;
  // Application answers captured at hunt creation (owner directive 2026-09-28)
  work_authorized?: boolean | null;
  needs_sponsorship?: boolean | null;
  notice_period?: string | null;
  earliest_start_date?: string | null;
  desired_salary_cents?: number | null;
  salary_currency?: string | null;
  salary_period?: string | null;
  leads_found: number;
  applications_prepared: number;
  applications_submitted: number;
  last_run_at?: string;
  last_status?: string;
  last_error?: string;
  last_summary?: string;
  created_at: string;
}

export interface JobAutopilotSettings {
  id: string;
  workspace_id: string;
  user_id: string;
  enabled: boolean;
  autonomy_mode: string;
  auto_apply_enabled?: boolean;
  valid_modes?: string[];
  per_run_cap: number;
  daily_cap: number;
  applications_today: number;
  notify_on_application: boolean;
  last_enabled_at?: string;
  last_disabled_at?: string;
  last_run_at?: string;
  last_status?: string;
  last_summary?: string;
  total_runs: number;
}

export interface ApplyProvider {
  provider: string;
  hosts: string[];
  auto_submit: 'full' | 'assisted' | 'unsupported';
  reason: string;
}

export const jobbotApi = {
  discover: {
    // Scans the workspace's active job sites and saves NEW leads from real web results.
    run: (data: { workspace_id?: string; site_id?: string; query?: string; location?: string; limit?: number }) =>
      http.post<DiscoveryResult>('/jobbot/discover', data),
    // Live progress of the in-flight scan (poll while `run` is still awaited).
    progress: () =>
      http.get<{ ok: boolean; run: DiscoverProgress | null }>('/jobbot/discover/progress'),
  },
  suggest: (workspaceId: string | undefined, kind: 'keywords' | 'roles' | 'companies' | 'locations', q: string, limit = 25) =>
    http.get<{ kind: string; suggestions: string[] }>('/jobbot/suggest', {
      params: { workspace_id: workspaceId, kind, q, limit },
    }),
  // The full curated job-title taxonomy (static, cached server-side).
  jobTitles: () =>
    http.get<{ total: number; categories: Array<{ name: string; titles: string[] }> }>('/jobbot/job-titles'),
  sites: {
    // Job is user-scoped: workspace_id is optional everywhere now.
    list: (workspaceId?: string) =>
      http.get<JobSite[]>('/jobbot/sites', { params: workspaceId ? { workspace_id: workspaceId } : {} }),
    categories: (workspaceId?: string) =>
      http.get<JobSiteCategory[]>('/jobbot/site-categories', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    // Admin only: user sites awaiting accept/decline.
    pendingReview: () =>
      http.get<JobSite[]>('/jobbot/sites/pending-review'),
    // What can we actually do with this job board URL? (four-tier check)
    check: (url: string) =>
      http.post<SiteCompatibilityVerdict>('/jobbot/sites/check', { url }),
    create: (data: { workspace_id?: string; name: string; url?: string; search_query?: string; is_active?: boolean; category?: string; scope?: string; submission_instructions?: string }) =>
      http.post<JobSite>('/jobbot/sites', data),
    update: (id: string, data: Partial<{ name: string; url: string; search_query: string; is_active: boolean; category: string; submission_instructions: string }>) =>
      http.put<JobSite>(`/jobbot/sites/${id}`, data),
    // Set (or clear) a site's "how to apply here" recipe. Empty string clears it.
    setInstructions: (id: string, submissionInstructions: string) =>
      http.put<JobSite>(`/jobbot/sites/${id}/submission-instructions`, { submission_instructions: submissionInstructions }),
    delete: (id: string) => http.delete(`/jobbot/sites/${id}`),
    // Submit an owned, compatible private site for the admin's review.
    submitReview: (id: string) =>
      http.post<JobSite>(`/jobbot/sites/${id}/submit-review`),
    // Admin decision: accept (promote to system) or decline.
    review: (id: string, decision: 'accept' | 'decline', note?: string) =>
      http.post<JobSite>(`/jobbot/sites/${id}/review`, { decision, note }),
  },
  leads: {
    list: (params: { workspace_id?: string; site_id?: string; status?: string; search_id?: string }) =>
      http.get<JobLead[]>('/jobbot/leads', { params }),
    get: (id: string) => http.get<JobLead>(`/jobbot/leads/${id}`),
    create: (data: { workspace_id?: string; site_id?: string; title: string; company?: string; location?: string; salary_range?: string; description?: string; url?: string; status?: string; search_id?: string }) =>
      http.post<JobLead>('/jobbot/leads', data),
    update: (id: string, data: Partial<JobLead>) =>
      http.put<JobLead>(`/jobbot/leads/${id}`, data),
    delete: (id: string) => http.delete(`/jobbot/leads/${id}`),
    // Verify open leads and move dead/declined/stale ones to their real status.
    // Never deletes. dry_run (default) previews without writing.
    cleanup: (data: { workspace_id?: string; dry_run?: boolean; stale_days?: number; check_open?: boolean; lead_ids?: string[] }) =>
      http.post<LeadCleanupResult>('/jobbot/leads/cleanup', data),
    // Prepare-and-queue an application for one lead. Submits NOTHING.
    prepare: (id: string, data: { workspace_id?: string; variant_id?: string; document_id?: string }) =>
      http.post<PreparedApplication>(`/jobbot/leads/${id}/prepare`, data),
  },
  applications: {
    list: (params: { workspace_id?: string; status?: string }) =>
      http.get<JobApplication[]>('/jobbot/applications', { params }),
    create: (data: { workspace_id?: string; lead_id?: string; resume_variant_id?: string; cover_letter?: string; status?: string; apply_email_account_id?: string }) =>
      http.post<JobApplication>('/jobbot/applications', data),
    update: (id: string, data: Partial<JobApplication>) =>
      http.put<JobApplication>(`/jobbot/applications/${id}`, data),
    delete: (id: string) => http.delete(`/jobbot/applications/${id}`),
    // Per-application proof: channel, submit time, evidence + why-no-confirmation.
    proof: (id: string) => http.get<ApplicationProof>(`/jobbot/applications/${id}/proof`),
    // Confirmation-email verification sweep (owner 2026-10-03): scan the job
    // mailbox and promote submitted → applied where a company confirmation is found.
    verify: () => http.post<{ ok: boolean; checked: number; verified: number; promoted: number }>('/jobbot/applications/verify'),
  },
  resumes: {
    list: (workspaceId?: string) =>
      http.get<ResumeDocument[]>('/jobbot/resumes', { params: workspaceId ? { workspace_id: workspaceId } : {} }),
    create: (data: { workspace_id?: string; title: string; content?: string; file_path?: string }) =>
      http.post<ResumeDocument>('/jobbot/resumes', data),
    update: (id: string, data: Partial<ResumeDocument>) =>
      http.put<ResumeDocument>(`/jobbot/resumes/${id}`, data),
    delete: (id: string) => http.delete(`/jobbot/resumes/${id}`),
    // Rewrite an existing resume into a NEW document (the original is kept).
    rewrite: (id: string, data: { workspace_id?: string; instructions?: string }) =>
      http.post<ResumeWriteResult>(`/jobbot/resumes/${id}/rewrite`, data),
    // Build a resume from the applicant profile + the user's own sections.
    build: (data: ResumeBuildPayload) =>
      http.post<ResumeWriteResult>('/jobbot/resumes/build', data),
    variants: {
      list: (docId: string) =>
        http.get<ResumeVariant[]>(`/jobbot/resumes/${docId}/variants`),
      create: (docId: string, data: { name: string; tailored_content?: string }) =>
        http.post<ResumeVariant>(`/jobbot/resumes/${docId}/variants`, data),
      update: (id: string, data: Partial<ResumeVariant>) =>
        http.put<ResumeVariant>(`/jobbot/variants/${id}`, data),
      delete: (id: string) => http.delete(`/jobbot/variants/${id}`),
    },
    upload: (
      workspaceId: string,
      file: File,
      opts?: { title?: string; buildProfile?: boolean },
    ) => {
      const form = new FormData();
      form.append('workspace_id', workspaceId);
      if (opts?.title) form.append('title', opts.title);
      form.append('build_profile', String(opts?.buildProfile ?? true));
      form.append('file', file);
      return http.post<{
        ok: boolean;
        document_id: string;
        title: string;
        file_type: string;
        parse_status: string;
        parse_error?: string | null;
        char_count: number;
        profile?: ApplicantProfile | null;
      }>('/jobbot/resumes/upload', form, {
        // Explicit multipart header: without it the axios instance default
        // (`application/json`) makes FastAPI reject the body with a 422 whose
        // `detail` is an ARRAY, which crashed the page (React error #31).
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
  },
  profile: {
    get: (workspaceId?: string) =>
      http.get<{ exists: boolean; profile: ApplicantProfile | null }>('/jobbot/profile', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    build: (workspaceId: string | undefined, documentId?: string) =>
      http.post<{ ok: boolean; profile: ApplicantProfile; method: string }>('/jobbot/profile/build', {
        workspace_id: workspaceId,
        document_id: documentId,
      }),
    update: (workspaceId: string | undefined, data: Partial<ApplicantProfile>) =>
      http.put<{ ok: boolean; profile: ApplicantProfile; resume_sync?: ResumeSyncReport }>('/jobbot/profile', data, {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
  },
  tailor: (data: { workspace_id?: string; lead_id: string; document_id?: string; hype_level?: string }) =>
    http.post<{ ok: boolean; variant_id: string; generation_method: string }>('/jobbot/tailor', data),
  // Named resume profiles (owner directive 2026-10-12).
  resumeProfiles: {
    list: (workspaceId?: string) =>
      http.get<ResumeProfile[]>('/jobbot/resume-profiles', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    create: (data: {
      workspace_id?: string;
      name: string;
      job_types: string[];
      summary_hint?: string | null;
      base_document_id?: string | null;
      hype_level?: string;
      is_default?: boolean;
    }) => http.post<ResumeProfile>('/jobbot/resume-profiles', data),
    update: (id: string, data: Partial<{
      name: string;
      job_types: string[];
      summary_hint: string | null;
      base_document_id: string | null;
      hype_level: string;
      is_default: boolean;
    }>) => http.patch<ResumeProfile>(`/jobbot/resume-profiles/${id}`, data),
    delete: (id: string) => http.delete(`/jobbot/resume-profiles/${id}`),
  },
  coverLetter: (data: { workspace_id?: string; lead_id: string; hype_level?: string }) =>
    http.post<{ ok: boolean; cover_letter: string; generation_method: string }>('/jobbot/cover-letter', data),
  hypeLevels: () =>
    http.get<{ levels: HypeLevel[]; default: string; note: string }>('/jobbot/hype-levels'),
  searches: {
    list: (workspaceId?: string) =>
      http.get<JobSearch[]>('/jobbot/searches', { params: workspaceId ? { workspace_id: workspaceId } : {} }),
    get: (id: string) =>
      http.get<JobSearch>(`/jobbot/searches/${id}`),
    create: (data: {
      workspace_id?: string;
      name: string;
      field?: string;
      keywords?: string;
      location?: string;
      sites?: string[];
      resume_document_id?: string;
      resume_variant_id?: string;
      resume_profile_id?: string;
      resume_strategy?: 'tailor_per_job' | 'fixed_resume';
      hype_level?: string;
      is_active?: boolean;
      autonomy_mode?: string;
      daily_cap?: number;
      seniority?: string | null;
      employment_type?: string | null;
      work_mode?: string | null;
      relocation?: boolean | null;
      radius_km?: number | null;
      date_posted?: string | null;
      exclude_keywords?: string | null;
      include_companies?: string | null;
      exclude_companies?: string | null;
      exclude_agencies?: boolean | null;
      salary_min_cents?: number | null;
      work_authorized?: boolean | null;
      needs_sponsorship?: boolean | null;
      notice_period?: string | null;
      earliest_start_date?: string | null;
      desired_salary_cents?: number | null;
      salary_currency?: string | null;
      salary_period?: string | null;
    }) => http.post<JobSearch>('/jobbot/searches', data),
    update: (id: string, data: Partial<JobSearch>) => http.put<JobSearch>(`/jobbot/searches/${id}`, data),
    // Run a hunt. `opts.include_unconfigured` opts a SINGLE run into scanning
    // sites that still need sign-in. When omitted (or false) we send NO params,
    // so the request is byte-identical to the prior behaviour.
    run: (id: string, opts?: { include_unconfigured?: boolean }) => {
      const url = `/jobbot/searches/${id}/run`;
      return opts?.include_unconfigured
        ? http.post<{ ok: boolean; created: number; summary?: string; reason?: string }>(
            url,
            undefined,
            { params: { include_unconfigured: true } },
          )
        : http.post<{ ok: boolean; created: number; summary?: string; reason?: string }>(url);
    },
  },
  autopilot: {
    get: (workspaceId?: string) =>
      http.get<{ exists: boolean; settings: JobAutopilotSettings | null; valid_modes?: string[] }>('/jobbot/autopilot', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    set: (data: {
      workspace_id?: string;
      enabled: boolean;
      autonomy_mode?: string;
      per_run_cap?: number;
      daily_cap?: number;
      acknowledge_auto_apply?: boolean;
    }) => http.put<{ ok: boolean; settings: JobAutopilotSettings }>('/jobbot/autopilot', data),
    providers: (workspaceId?: string) =>
      http.get<{ ok: boolean; autonomy_mode: string; valid_modes: string[]; providers: ApplyProvider[] }>(
        '/jobbot/apply/providers',
        { params: workspaceId ? { workspace_id: workspaceId } : {} },
      ),
    run: (data: { workspace_id?: string; dry_run?: boolean; search_id?: string }) =>
      http.post<{ ok: boolean; summary: string; prepared: number; submitted: number }>(
        '/jobbot/autopilot/run',
        data,
      ),
    runs: (workspaceId?: string) =>
      http.get<Array<{ id: string; status: string; summary?: string; started_at?: string }>>(
        '/jobbot/autopilot/runs',
        { params: workspaceId ? { workspace_id: workspaceId } : {} },
      ),
  },
  applicationsPending: (workspaceId?: string) =>
    http.get<PendingApplication[]>('/jobbot/applications/pending', {
      params: workspaceId ? { workspace_id: workspaceId } : {},
    }),
  approveApplication: (data: {
    workspace_id?: string;
    application_id: string;
    approve: boolean;
    submit?: boolean;
  }) =>
    http.post<{ ok: boolean; sent: boolean; detail: string; approval_status: string }>(
      '/jobbot/applications/approve',
      data,
    ),
  extension: {
    // Guided job-site authentication registry (owner directive 2026-09-28).
    connections: (workspaceId?: string) =>
      http.get<JobSiteConnectionOverview>('/job-connections', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    revokeConnection: (connectionId: string) =>
      http.delete<{ ok: boolean; revoked: string }>(`/job-connections/${connectionId}`),
    // Read-only job-site auth summary. The backend endpoint is FLAG-GATED
    // (SAIK_SITE_STATUS_BOARD_ENABLED); it returns enabled:false when OFF, in
    // which case callers must render nothing.
    siteStatusBoard: (opts?: { signal?: AbortSignal }) =>
      http.get<SiteStatusBoardResponse>('/job-connections/site-status-board', {
        signal: opts?.signal,
      }),
    // Read-only in-app apply-readiness plan. The backend endpoint is FLAG-GATED
    // (SAIK_INAPP_APPLY_RUN_ENABLED); it returns enabled:false when OFF, in
    // which case callers must render nothing.
    applyRunPlan: (opts?: { signal?: AbortSignal }) =>
      http.get<ApplyRunPlanResponse>('/job-connections/apply-run-plan', {
        signal: opts?.signal,
      }),
    // Read-only guided sign-in verification queue. The backend endpoint is
    // FLAG-GATED (SAIK_GUIDED_VERIFY_ENABLED); it returns enabled:false when
    // OFF, in which case callers must render nothing.
    verificationQueue: (opts?: { signal?: AbortSignal }) =>
      http.get<VerificationQueueResponse>('/job-connections/verification-queue', {
        signal: opts?.signal,
      }),
    // Read-only legacy plug-in queue-migration readiness. The backend endpoint
    // is FLAG-GATED (SAIK_QUEUE_MIGRATION_ENABLED); it returns enabled:false
    // when OFF, in which case callers must render nothing.
    queueMigration: (opts?: { signal?: AbortSignal }) =>
      http.get<QueueMigrationResponse>('/job-connections/queue-migration', {
        signal: opts?.signal,
      }),
    // Read-only legacy plug-in token-retirement readiness. The backend endpoint
    // is FLAG-GATED (SAIK_EXTENSION_RETIREMENT_ENABLED); it returns
    // enabled:false when OFF, in which case callers must render nothing.
    extensionRetirement: (opts?: { signal?: AbortSignal }) =>
      http.get<ExtensionRetirementResponse>('/job-connections/extension-retirement', {
        signal: opts?.signal,
      }),
    // Read-only in-app go-live readiness report. The backend endpoint is
    // FLAG-GATED (SAIK_GO_LIVE_READINESS_ENABLED); it returns enabled:false when
    // OFF, in which case callers must render nothing.
    goLiveReadiness: (opts?: { signal?: AbortSignal }) =>
      http.get<GoLiveReadinessResponse>('/job-connections/go-live-readiness', {
        signal: opts?.signal,
      }),
  },
  interviews: {
    list: (params: { workspace_id?: string; status?: string }) =>
      http.get<JobInterview[]>('/jobbot/interviews', { params }),
    create: (data: { workspace_id?: string; title: string; scheduled_at?: string; location?: string; mode?: string }) =>
      http.post<JobInterview>('/jobbot/interviews', data),
    update: (id: string, data: Partial<JobInterview>) =>
      http.put<JobInterview>(`/jobbot/interviews/${id}`, data),
  },
  replies: {
    scan: (data: { workspace_id?: string; limit?: number }) =>
      http.post<{ ok: boolean; processed: number; interviews_created: number; unmatched: number }>(
        '/jobbot/replies/scan',
        data,
      ),
  },
  correspondence: {
    list: (workspaceId?: string) =>
      http.get<JobCorrespondence[]>('/jobbot/correspondence', {
        params: workspaceId ? { workspace_id: workspaceId } : {},
      }),
    draft: (data: { workspace_id?: string; correspondence_id: string; kind?: string }) =>
      http.post<{ ok: boolean; draft: string; sent: boolean }>('/jobbot/correspondence/draft', data),
    send: (data: { workspace_id?: string; correspondence_id: string; confirm: boolean }) =>
      http.post<{ ok: boolean; sent: boolean; detail: string }>('/jobbot/correspondence/send', data),
  },
  journal: (workspaceId?: string, limit = 40) =>
    http.get<{ steps: Array<{ id: string; task?: string; content: string; created_at?: string }> }>(
      '/jobbot/journal',
      { params: { ...(workspaceId ? { workspace_id: workspaceId } : {}), limit } },
    ),
};